import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { useApi } from "../../hooks/useApi";
import { useStateValue } from "../../context/StateValueContext";
import { MiniCompany } from "../../types/company";
import { PERMISSIONS } from "../../constants/permissions";
import CompanySelect from "../common/CompanySelect";

interface MerchantRowProps {
  merchant: string;
  companies: MiniCompany[];
  onMapped: () => void;
  mappingUrl: (companyId: number) => string;
  remapUrl: string;
  saveInProgress: boolean;
  onSavingChange: (saving: boolean) => void;
}

function MerchantRow({ merchant, companies, onMapped, mappingUrl, remapUrl, saveInProgress, onSavingChange }: MerchantRowProps) {
  const { post } = useApi();
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | "">("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mapped, setMapped] = useState(false);

  const handleMap = async () => {
    if (!selectedCompanyId) return;

    setError(null);
    setSubmitting(true);
    onSavingChange(true);

    try {
      await post(mappingUrl(selectedCompanyId), {
        merchantName: merchant,
        companyId: selectedCompanyId,
      });
      if (remapUrl) {
        await post(remapUrl, {}, { slowApi: true });
      }
      setMapped(true);
      onMapped();
    } catch (err: any) {
      setError(err.message || "Failed to map merchant");
    } finally {
      setSubmitting(false);
      onSavingChange(false);
    }
  };

  if (mapped) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          py: 1.5,
          px: 2,
          borderRadius: 1,
          border: "1px solid #bbf7d0",
        }}
      >
        <CheckIcon sx={{ color: "#16a34a", fontSize: 20 }} />
        <Typography variant="body2" sx={{ color: "#16a34a", fontWeight: 500 }}>
          {merchant} — mapped successfully
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        py: 1.5,
        px: 2,
        borderRadius: 1,
        border: "1px solid #e5e7eb",
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          minWidth: 160,
          pt: '8px',
          wordBreak: "break-word",
        }}
      >
        {merchant}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 180 }}>
        <CompanySelect
          companies={companies}
          value={selectedCompanyId}
          onChange={(companyId) => {
            setSelectedCompanyId(companyId);
            setError(null);
          }}
          disabled={submitting}
        />
        {error && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
            {error}
          </Typography>
        )}
      </Box>
      <Button
        variant="contained"
        size="small"
        onClick={handleMap}
        disabled={submitting || saveInProgress || !selectedCompanyId}
        sx={{
          mt: '4px',
          minWidth: 64,
          backgroundColor: "#465fff",
          "&:hover": { backgroundColor: "#3641f5" },
        }}
      >
        {submitting ? <CircularProgress size={18} color="inherit" /> : "Save"}
      </Button>
    </Box>
  );
}

export interface AlertProviderConfig {
  label: string;
  unassignedUrl: string;
  mappingUrl: (companyId: number) => string;
  remapUrl: string;
}

export const ETHOCA_PROVIDER_CONFIG: AlertProviderConfig = {
  label: "Ethoca",
  unassignedUrl: "/api/alerts/ethoca/merchants/unassigned",
  mappingUrl: (companyId) => `/api/companies/${companyId}/ethoca/merchant-mappings`,
  remapUrl: "/api/alerts/ethoca/remap-unmapped",
};

export const CDRN_PROVIDER_CONFIG: AlertProviderConfig = {
  label: "CDRN",
  unassignedUrl: "/api/alerts/cdrn/merchants/unassigned",
  mappingUrl: (companyId) => `/api/companies/${companyId}/cdrn/merchant-mappings`,
  remapUrl: "/api/alerts/cdrn/remap-unmapped",
};

export const RDR_PROVIDER_CONFIG: AlertProviderConfig = {
  label: "RDR Notices",
  unassignedUrl: "/api/alerts/verifi-notices/merchants/unassigned",
  mappingUrl: (companyId) => `/api/companies/${companyId}/verifi-notices/merchant-mappings`,
  remapUrl: "/api/alerts/verifi-notices/remap-unmapped",
};

export function useUnassignedMerchants(provider: AlertProviderConfig) {
  const { get } = useApi();
  const [{ user }] = useStateValue();
  const [merchants, setMerchants] = useState<string[]>([]);

  const hasPermission = user?.permissions?.includes(PERMISSIONS.ALERT_MERCHANT_TO_COMPANY);

  const fetch = useCallback(async () => {
    if (!hasPermission) return;
    try {
      const response = await get<{ merchants: { name: string }[] }>(
        provider.unassignedUrl
      );
      setMerchants(response.merchants.map((m) => m.name));
    } catch {
      // Silently ignore
    }
  }, [get, provider.unassignedUrl, hasPermission]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { merchants, refresh: fetch };
}

interface MapMerchantToCompanyDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  provider: AlertProviderConfig;
  merchants: string[];
}

export default function MapMerchantToCompanyDialog({
  open,
  onClose,
  onSuccess,
  provider,
  merchants,
}: MapMerchantToCompanyDialogProps) {
  const [{ miniCompanies }] = useStateValue();
  const [anyMapped, setAnyMapped] = useState(false);
  const [saveInProgress, setSaveInProgress] = useState(false);

  useEffect(() => {
    if (open) {
      setAnyMapped(false);
      setSaveInProgress(false);
    }
  }, [open]);

  const handleClose = () => {
    if (anyMapped) {
      onSuccess();
    }
    onClose();
  };

  const handleDialogClose = (_event: object, reason: string) => {
    if (reason !== "backdropClick") {
      handleClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
      <DialogTitle>Link Merchant to Company</DialogTitle>
      <DialogContent>
        {merchants.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No unassigned merchants found.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 0.5 }}>
            {merchants.map((merchant) => (
              <MerchantRow
                key={merchant}
                merchant={merchant}
                companies={miniCompanies}
                onMapped={() => setAnyMapped(true)}
                mappingUrl={provider.mappingUrl}
                remapUrl={provider.remapUrl}
                saveInProgress={saveInProgress}
                onSavingChange={setSaveInProgress}
              />
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
